import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Loader2, Download, Upload, FileCheck, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetNotaryInvestigationQuery, useUploadFinalAffidavitMutation } from '@/store/api/apiSlice';
import { resolveFileUrl } from '@/utils/fileUrl';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import { validateDocumentOnlyFile } from '@/utils/fileValidation';
import type { AffidavitFile } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() || 'NT';
}

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// Files live under /uploads/* and are served publicly — download without an auth
// header (the header would force a CORS preflight the static host doesn't answer).
async function downloadFile(file: AffidavitFile, onError: (m: string) => void) {
    const absoluteUrl = resolveFileUrl(file.fileUrl);
    try {
        const res = await fetch(absoluteUrl, { method: 'GET' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName || 'affidavit';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 1000);
    } catch {
        const opened = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
        if (!opened) onError('Could not download the file. Please try again.');
    }
}

export default function NotaryDetail() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data, isLoading, isError } = useGetNotaryInvestigationQuery(id, { skip: !id });
    const [uploadFinal, { isLoading: isUploading }] = useUploadFinalAffidavitMutation();

    const inputRef = useRef<HTMLInputElement>(null);
    const [selected, setSelected] = useState<File | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handlePick = (file: File | null) => {
        setError('');
        setSuccess('');
        if (!file) {
            setSelected(null);
            return;
        }
        const validationError = validateDocumentOnlyFile(file);
        if (validationError) {
            setError(validationError);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        setSelected(file);
    };

    const handleUpload = async () => {
        if (!selected || !id) return;
        setError('');
        try {
            await uploadFinal({ id, file: selected }).unwrap();
            setSelected(null);
            if (inputRef.current) inputRef.current.value = '';
            setSuccess('Notarised affidavit uploaded. The employee has been emailed the final copy.');
        } catch (err: any) {
            setError(parseErrorMessage(err, 'Could not upload the notarised affidavit. Please try again.'));
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Notary'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'NT'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10">
                    <button
                        onClick={() => navigate('/notary/investigations')}
                        className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to assignments
                    </button>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-24">
                            <Loader2 className="h-7 w-7 text-gray-400 animate-spin" />
                        </div>
                    ) : isError || !data ? (
                        <div className="max-w-md mx-auto text-center py-16">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                                <AlertCircle className="h-7 w-7 text-red-500" />
                            </div>
                            <h1 className="text-lg font-bold text-gray-900">Assignment not available</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                This investigation isn't assigned to you, or the link is invalid.
                            </p>
                        </div>
                    ) : (
                        <div className="max-w-3xl mx-auto flex flex-col gap-6">
                            {/* Header */}
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {[data.employee.firstName, data.employee.middleName, data.employee.lastName]
                                            .filter(Boolean)
                                            .join(' ') || 'Employee'}
                                    </h1>
                                    <p className="text-sm text-gray-500">{data.employer.companyName || 'Employer'}</p>
                                </div>
                                {data.notaryCompletedAt ? (
                                    <Badge
                                        variant="outline"
                                        className="bg-green-50 text-green-700 border-green-100 px-3 py-1 rounded-lg font-medium"
                                    >
                                        Notarised ✓ {new Date(data.notaryCompletedAt).toLocaleDateString()}
                                    </Badge>
                                ) : (
                                    <Badge
                                        variant="outline"
                                        className="bg-amber-50 text-amber-700 border-amber-100 px-3 py-1 rounded-lg font-medium"
                                    >
                                        Pending
                                    </Badge>
                                )}
                            </div>

                            {/* Basic info */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 rounded-2xl border border-gray-100 p-5">
                                <InfoBlock title="Employee">
                                    <Line label="Email" value={data.employee.email} />
                                    <Line label="Phone" value={data.employee.phone} />
                                    <Line label="Gender" value={data.employee.gender} />
                                </InfoBlock>
                                <InfoBlock title="Employer">
                                    <Line label="Company" value={data.employer.companyName} />
                                    <Line label="Contact" value={data.employer.contactName} />
                                    <Line label="Email" value={data.employer.email} />
                                    <Line label="Phone" value={data.employer.phone} />
                                </InfoBlock>
                            </div>

                            {/* Signed affidavit — review */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <h2 className="text-sm font-bold text-gray-900 mb-3">
                                    Employee's signed affidavit review this
                                </h2>
                                {data.signedAffidavit ? (
                                    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                                        <span className="flex items-center gap-2 min-w-0">
                                            <FileCheck className="h-4 w-4 shrink-0 text-gray-500" />
                                            <span className="truncate text-sm text-gray-800">
                                                {data.signedAffidavit.fileName}
                                            </span>
                                            <span className="hidden sm:inline text-xs text-gray-400 shrink-0">
                                                {formatWhen(data.signedAffidavit.uploadedAt)}
                                            </span>
                                        </span>
                                        <Button
                                            onClick={() => downloadFile(data.signedAffidavit!, setError)}
                                            className="shrink-0 flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white hover:bg-gray-800"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            Download
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        The employee hasn't uploaded their signed affidavit yet. You'll be able to
                                        upload the notarised copy once they do.
                                    </p>
                                )}
                            </div>

                            {/* Upload final notarised copy */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <h2 className="text-sm font-bold text-gray-900 mb-3">Upload the notarised affidavit</h2>

                                {data.notarizedAffidavit && (
                                    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-green-100 bg-green-50 px-3 py-2.5">
                                        <span className="flex items-center gap-2 min-w-0">
                                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                                            <span className="truncate text-sm text-green-800">
                                                {data.notarizedAffidavit.fileName}
                                            </span>
                                        </span>
                                        <Button
                                            onClick={() => downloadFile(data.notarizedAffidavit!, setError)}
                                            className="shrink-0 flex items-center gap-1.5 rounded-lg border border-green-200 bg-white px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            Download
                                        </Button>
                                    </div>
                                )}

                                {!data.signedAffidavit ? (
                                    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                        Waiting for the employee to submit their signed affidavit. You can upload the
                                        notarised copy once that's done.
                                    </p>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {selected ? (
                                            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                                                <FileCheck className="h-4 w-4 shrink-0 text-green-600" />
                                                <span className="flex-1 truncate text-sm text-gray-800">
                                                    {selected.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePick(null)}
                                                    className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50"
                                                    aria-label="Remove file"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => inputRef.current?.click()}
                                                className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2.5 text-left text-sm text-gray-600 transition-colors hover:border-[#FF6B35] hover:text-[#FF6B35]"
                                            >
                                                <Upload className="h-4 w-4 shrink-0" />
                                                <span className="flex-1">
                                                    {data.notarizedAffidavit
                                                        ? 'Replace notarised affidavit (PDF or Word)'
                                                        : 'Upload notarised affidavit (PDF or Word)'}
                                                </span>
                                            </button>
                                        )}

                                        <input
                                            ref={inputRef}
                                            type="file"
                                            accept="application/pdf,.pdf,.doc,.docx"
                                            onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
                                            className="hidden"
                                        />

                                        <Button
                                            onClick={handleUpload}
                                            disabled={!selected || isUploading}
                                            className="self-start flex items-center gap-2 rounded-lg bg-[#FF6B35] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#e0581f] disabled:opacity-50"
                                        >
                                            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {data.notaryCompletedAt ? 'Replace & notify employee' : 'Upload & complete'}
                                        </Button>
                                    </div>
                                )}

                                {error && (
                                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                                )}
                                {success && (
                                    <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                                        {success}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

function InfoBlock({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">{title}</p>
            <div className="flex flex-col gap-1.5">{children}</div>
        </div>
    );
}

function Line({ label, value }: { label: string; value?: string | undefined }) {
    return (
        <p className="text-sm">
            <span className="text-gray-500">{label}: </span>
            <span className="font-medium text-gray-900">{value || '—'}</span>
        </p>
    );
}
